import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { HomeIcon, TrendingUpIcon, BuildingIcon, CheckCircle2Icon, CoinsIcon, PercentIcon, CalendarIcon, UsersIcon, SparklesIcon, ZapIcon, ShieldCheckIcon, TrendingDownIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownIcon } from 'lucide-react';
export function Reconversion() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [rentalRate, setRentalRate] = useState(12);
  const [expandedCards, setExpandedCards] = useState({
    monthly: false,
    annual: false,
    currentROI: false,
    resaleROI: false
  });
  // Fixed investment amount
  const investmentAmount = 3612460;
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: HomeIcon
  }, {
    id: 'advantages',
    label: 'Advantages',
    icon: SparklesIcon
  }];
  const advantages = [{
    icon: CalendarIcon,
    title: 'Reconversie etapizată',
    description: 'Poate fi realizată etapizat, pe măsură ce spațiile se eliberează',
    color: '#357beb'
  }, {
    icon: TrendingDownIcon,
    title: 'Costuri reduse',
    description: 'Costuri de transformare reduse datorită infrastructurii deja existente',
    color: '#10b981'
  }, {
    icon: HomeIcon,
    title: 'Penthouse premium',
    description: 'Terasa de la ultimul nivel poate fi valorificată pentru un apartament tip penthouse',
    color: '#f59e0b'
  }, {
    icon: UsersIcon,
    title: 'Cerere ridicată',
    description: 'Cerere ridicată pentru locuințe în zona Unirii – ideal pentru vânzare sau închiriere',
    color: '#8b5cf6'
  }, {
    icon: TrendingUpIcon,
    title: 'Creștere valoare',
    description: 'Creșterea valorii totale prin vânzarea unităților rezidențiale',
    color: '#357beb'
  }, {
    icon: CoinsIcon,
    title: 'Randament superior',
    description: 'Randament potențial de revânzare superior (peste 30–35%)',
    color: '#10b981'
  }, {
    icon: ZapIcon,
    title: 'Investiție flexibilă',
    description: 'Adaptabilă pieței (office astăzi, rezidențial mâine)',
    color: '#f59e0b'
  }, {
    icon: ShieldCheckIcon,
    title: 'Infrastructură modernă',
    description: 'Lift modern cu acces la ambele corpuri, rețele, grupuri sanitare și instalații complete',
    color: '#8b5cf6'
  }];
  const floorPlans = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9LKqGYNeSvofEcfX61B1HX/image.png',
    title: 'Plan Etaj Tip 1',
    description: 'Configurație cu 4 apartamente per etaj'
  }, {
    url: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800',
    title: 'Plan Etaj Tip 2',
    description: 'Configurație cu 3 apartamente per etaj + spații comune'
  }, {
    url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800',
    title: 'Plan Penthouse',
    description: 'Apartament premium cu terasă panoramică'
  }];
  // ROI Calculator logic
  const totalArea = 2500;
  const monthlyRentalIncome = totalArea * rentalRate;
  const annualRentalIncome = monthlyRentalIncome * 12;
  const currentROI = annualRentalIncome / investmentAmount * 100;
  const potentialResaleValue = investmentAmount * 1.35;
  const potentialProfit = potentialResaleValue - investmentAmount;
  const potentialResaleROI = 35;
  const fiveYearProfit = annualRentalIncome * 5 + potentialProfit;
  const tabContentVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      scale: 1
    }
  };
  const toggleCard = (cardId: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };
  return <section className="py-20 px-6 md:px-12 lg:px-16 bg-gray-50" ref={ref} data-id="element-429">
      <div className="max-w-7xl mx-auto" data-id="element-430">
        <motion.h2 className="text-3xl md:text-5xl font-bold text-[#132c53] text-center mb-4" initial={{
        y: -50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-431">
          Posibilitate de conversie în{' '}
          <span className="text-[#357beb]" data-id="element-432">clădire rezidențială</span>
        </motion.h2>
        <motion.p className="text-gray-700 text-lg text-center mb-12 max-w-4xl mx-auto" initial={{
        y: 30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-433">
          Clădirea Cuza Vodă 50–52 oferă o oportunitate excelentă de reconversie
          în imobil rezidențial, cu apartamente moderne, adaptate cererii
          actuale din zona centrală a Bucureștiului.
        </motion.p>
        {/* Tabs */}
        <motion.div className="bg-white rounded-xl shadow-lg overflow-hidden" initial={{
        y: 50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.4
      }} data-id="element-434">
          <div className="flex overflow-x-auto border-b border-gray-200" data-id="element-435">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex-1 min-w-fit py-4 px-6 text-sm md:text-base font-medium transition-all relative hover:bg-gray-50 flex items-center justify-center gap-2" style={{
            color: activeTab === tab.id ? '#357beb' : '#6b7280'
          }} data-id="element-436">
                <tab.icon className="w-5 h-5" data-id="element-437" />
                <span data-id="element-438">{tab.label}</span>
                {activeTab === tab.id && <motion.div layoutId="activeReconversionTab" className="absolute bottom-0 left-0 right-0 h-1 bg-[#357beb]" transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-439" />}
              </button>)}
          </div>
          {/* Tab Content */}
          <div className="p-8 md:p-12 min-h-[500px]" data-id="element-440">
            <AnimatePresence mode="wait" data-id="element-441">
              {/* Overview Tab */}
              {activeTab === 'overview' && <motion.div key="overview" variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" data-id="element-442">
                  <div className="grid lg:grid-cols-2 gap-12 items-start" data-id="element-443">
                    <div data-id="element-444">
                      <h3 className="text-2xl font-bold text-[#132c53] mb-6" data-id="element-445">
                        Configurație flexibilă pentru transformare eficientă
                      </h3>
                      <p className="text-gray-700 text-lg mb-8 leading-relaxed" data-id="element-446">
                        Datorită configurației flexibile a spațiilor, structurii
                        solide din beton armat și accesului dublu (două intrări,
                        un lift cu acces la ambele corpuri), imobilul permite o
                        compartimentare eficientă pe etaje, fără modificări
                        structurale majore.
                      </p>
                      <div className="bg-gradient-to-br from-[#357beb] to-[#2868d9] p-6 rounded-xl" data-id="element-447">
                        <h4 className="font-semibold text-white text-xl mb-4" data-id="element-448">
                          Beneficii pentru investitori:
                        </h4>
                        <div className="space-y-3" data-id="element-449">
                          <div className="flex items-start gap-3" data-id="element-450">
                            <CheckCircle2Icon className="w-5 h-5 text-white flex-shrink-0 mt-1" data-id="element-451" />
                            <p className="text-white" data-id="element-452">
                              Creșterea valorii totale a activului prin vânzarea
                              unităților rezidențiale
                            </p>
                          </div>
                          <div className="flex items-start gap-3" data-id="element-453">
                            <CheckCircle2Icon className="w-5 h-5 text-white flex-shrink-0 mt-1" data-id="element-454" />
                            <p className="text-white" data-id="element-455">
                              Randament potențial de revânzare superior (peste
                              30–35%)
                            </p>
                          </div>
                          <div className="flex items-start gap-3" data-id="element-456">
                            <CheckCircle2Icon className="w-5 h-5 text-white flex-shrink-0 mt-1" data-id="element-457" />
                            <p className="text-white" data-id="element-458">
                              Investiție flexibilă, adaptabilă pieței (office
                              astăzi, rezidențial mâine)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="relative" data-id="element-459">
                      <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl bg-white p-4" data-id="element-460">
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9LKqGYNeSvofEcfX61B1HX/image.png" alt="Propunere de reconversie rezidențială" className="w-full h-full object-contain" data-id="element-461" />
                      </div>
                    </div>
                  </div>
                </motion.div>}
              {/* Advantages Tab */}
              {activeTab === 'advantages' && <motion.div key="advantages" variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" data-id="element-462">
                  <h3 className="text-2xl font-bold text-[#132c53] mb-8 text-center" data-id="element-463">
                    Avantaje cheie ale reconversiei
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-464">
                    {advantages.map((advantage, index) => <motion.div key={index} className="bg-white border border-gray-200 p-6 rounded-xl hover:shadow-xl transition-all duration-300 cursor-pointer" variants={cardVariants} initial="hidden" animate="visible" transition={{
                  delay: index * 0.1
                }} whileHover={{
                  y: -5
                }} data-id="element-465">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                    backgroundColor: `${advantage.color}20`
                  }} data-id="element-466">
                          <advantage.icon className="w-6 h-6" style={{
                      color: advantage.color
                    }} data-id="element-467" />
                        </div>
                        <h4 className="font-bold text-[#132c53] mb-2" data-id="element-468">
                          {advantage.title}
                        </h4>
                        <p className="text-gray-600 text-sm" data-id="element-469">
                          {advantage.description}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>;
}